import { Box, Paper, Skeleton, Stack, Typography } from '@mui/material'
import KpiBigNumber from './KpiBigNumber'
import { bucketColor } from './BucketColors'
import InfoTooltip from './InfoTooltip'

interface OpenByTypeCardProps {
  title: string
  // null signals "data not loaded yet" — buckets render as Skeleton placeholders.
  buckets: Record<string, number> | null
  bucketOrder: string[]
  // Optional display labels per bucket key (e.g. {A: 'A-feil'}). Falls back to the key.
  bucketLabels?: Record<string, string>
  // Event-type accent color (TYPE_COLORS / typeColor) shown as a left rail on the card.
  typeAccentColor?: string
  variant?: 'compact' | 'wall'
  info?: string
  onBucketClick?: (bucket: string) => void
}

function OpenByTypeCard({ title, buckets, bucketOrder, bucketLabels, typeAccentColor, variant = 'compact', info, onBucketClick }: OpenByTypeCardProps) {
  const isWall = variant === 'wall'
  const total = buckets ? bucketOrder.reduce((sum, key) => sum + (buckets[key] ?? 0), 0) : null
  return (
    <Paper
      elevation={1}
      sx={{
        p: isWall ? { xs: 2, md: 3 } : 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: typeAccentColor ? `4px solid ${typeAccentColor}` : undefined,
      }}
    >
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <Typography
            variant={isWall ? undefined : 'h6'}
            sx={{ fontSize: isWall ? 'clamp(1.25rem, 2vw, 2.25rem)' : undefined, fontWeight: 600 }}
          >
            {title}
          </Typography>
          {info && !isWall && <InfoTooltip text={info} />}
        </Stack>
        <Typography
          sx={{ fontSize: isWall ? 'clamp(1.5rem, 2.5vw, 2.75rem)' : undefined, fontWeight: 700 }}
          variant={isWall ? undefined : 'h6'}
          color="text.secondary"
        >
          {total === null ? <Skeleton variant="text" width={32} sx={{ display: 'inline-block' }} /> : total}
        </Typography>
      </Stack>
      <Box
        sx={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(bucketOrder.length, isWall ? 4 : 3)}, 1fr)`,
          gap: isWall ? { xs: 1.5, md: 2 } : 1,
        }}
      >
        {bucketOrder.map((bucket) => (
          <KpiBigNumber
            key={bucket}
            label={bucketLabels?.[bucket] ?? bucket}
            value={buckets === null ? null : buckets[bucket] ?? 0}
            variant={variant}
            accentColor={bucketColor(bucket)}
            onClick={onBucketClick ? () => onBucketClick(bucket) : undefined}
            dense={isWall}
          />
        ))}
      </Box>
    </Paper>
  )
}

export default OpenByTypeCard
